import { type JSX } from 'react';

import { type AdfoxContextProviderProps } from '@/packages/adfox';
import { AdfoxParallaxBanner } from '@/packages/adfox/AdfoxParallaxBanner/AdfoxParallaxBanner';
import { AdfoxTGBBanner } from '@/packages/adfox/AdfoxTGBBanner/AdfoxTGBBanner';
import { memoWithDisplayName } from '@/packages/react-utils/memoWithDisplayName';
import { useReduxSelector } from '@/packages/redux-state/store';
import { isCommercialSelector } from '@/packages/redux-state/store/slices/jsonQuery/selectors/isCommercialSelector';
import { useListingLayoutIsNarrowSelector } from '@/packages/redux-state/store/slices/listingLayout/selectors/dumb';
import { isLastListItem } from '@/packages/utils/array';

import { type IOfferForDesktopEnrichedV3Schema } from '@/repositories/generated/shared/search-offers/entities/offer_for_desktop/OfferForDesktopEnrichedV3Schema';

import {
  AdfoxParallaxBannerWrapper,
  AdfoxTgbBannerWrapper,
  CardList,
  CardListItem,
} from '../components/CardListLayout';
import { type TCardApiContext } from '../types/TCardApiContext';
import { type TCardContextData } from '../types/TCardContextData';

import { ResidentialCardContainer as Card } from './ResidentialCardContainer';

type TCardsWithAdsContainerProps = {
  adfox: AdfoxContextProviderProps['serverData'];
  dealType: 'sale' | 'rent';
  offers: IOfferForDesktopEnrichedV3Schema[];
  initCardApi: (cardData: TCardContextData) => TCardApiContext;
  renderAdvertisment?: (props: { index: number; previousOfferId: number }) => JSX.Element;
};

export const CardsWithAdsContainer = memoWithDisplayName<TCardsWithAdsContainerProps>(props => {
  const { dealType, offers, adfox, initCardApi } = props;
  const isNarrow = useListingLayoutIsNarrowSelector();
  const isCommercial = useReduxSelector(isCommercialSelector);

  return (
    <CardList isNarrow={isNarrow} isResponsive={!isCommercial}>
      {offers.reduce((accumulator, offer, index, offersArr) => {
        accumulator.push(
          <CardListItem key={offer.id}>
            <Card dealType={dealType} index={index} initCardApi={initCardApi} offer={offer} />
          </CardListItem>,
        );

        if (isLastListItem(offersArr, index)) {
          return accumulator;
        }

        /**
         * @description каждое 10 объявление: 10, 20, 30, ...
         */
        if ((index + 1) % 10 === 0) {
          accumulator.push(
            <AdfoxParallaxBannerWrapper key={`adfox_parallax_banner_next_to_${index + 1}`}>
              <AdfoxParallaxBanner isNarrow={isNarrow} position={index + 1} serverData={adfox} />
            </AdfoxParallaxBannerWrapper>,
          );
          /**
           * @description каждое 10 объявление если начинать с 5: 5, 15, 25, ...
           */
        } else if ((index + 1) % 10 === 5) {
          accumulator.push(
            <AdfoxTgbBannerWrapper key={`adfox_tgb_banner_next_to_${index + 1}`}>
              <AdfoxTGBBanner isNarrow={isNarrow} position={index + 1} serverData={adfox} />
            </AdfoxTgbBannerWrapper>,
          );
        }

        return accumulator;
      }, Array.of<JSX.Element>())}
    </CardList>
  );
}, 'Cards');
